import React from 'react'
import Skeleton from '@mui/material/Skeleton'
import styled from '@emotion/styled'
import theme from '../../global/theme'

// ─── Layout (mirrors Container + FilterWrapper + CardsWrapper) ──────────────

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  justify-content: center;
`

const FilterPlaceholder = styled.div`
  min-width: 350px;

  @media screen and (max-width: 950px) {
    min-width: 0;
    width: 0;
    display: none;
  }
`

const FilterSidebarFixed = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 350px;
  padding: 100px 40px 40px;
  border-right: 1px solid ${theme.colors.borderColor};
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  gap: 28px;
  overflow: hidden;

  @media screen and (max-width: 950px) {
    display: none;
  }
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const CardsArea = styled.div`
  flex: 1;
  max-width: 1200px;
  padding: 20px 60px;

  @media screen and (max-width: 950px) {
    padding: 20px;
  }
`

// ─── Single plan card skeleton ───────────────────────────────────────────────

const Card = styled.div`
  max-width: 1200px;
  width: 100%;
  border: 1px solid ${theme.colors.borderColor};
  border-radius: 16px;
  overflow: hidden;
  margin: 20px 0;
`

const CardHeader = styled.div`
  padding: 20px 40px;
  border-bottom: 1px solid ${theme.colors.borderColor};
  display: flex;
  flex-direction: column;
  gap: 12px;

  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 20px;
  }
  .left {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  @media screen and (max-width: 600px) {
    padding: 14px 20px;
  }
`

const PremiumRow = styled.div`
  padding: 20px 40px;
  border-bottom: 1px solid ${theme.colors.borderColor};
  display: flex;
  align-items: center;
  gap: 24px;

  @media screen and (max-width: 600px) {
    padding: 14px 20px;
    flex-wrap: wrap;
  }
`

const BenefitsGrid = styled.div`
  padding: 20px 40px;
  border-bottom: 1px solid ${theme.colors.borderColor};
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media screen and (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 600px) {
    padding: 14px 20px;
  }
`

const BenefitCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const CardFooter = styled.div`
  padding: 16px 40px;
  display: flex;
  gap: 16px;
  align-items: center;

  @media screen and (max-width: 600px) {
    padding: 12px 20px;
  }
`

const SingleCardSkeleton: React.FC = () => (
  <Card>
    <CardHeader>
      <div className='title-row'>
        <div className='left'>
          <Skeleton
            variant='rectangular'
            width={100}
            height={36}
            sx={{ borderRadius: '6px' }}
          />
          <Skeleton variant='text' width={220} height={28} />
        </div>
        <Skeleton
          variant='rectangular'
          width={80}
          height={28}
          sx={{ borderRadius: '20px' }}
        />
      </div>
      <Skeleton variant='text' width={160} height={18} />
    </CardHeader>

    <PremiumRow>
      <Skeleton variant='text' width={130} height={20} />
      <Skeleton
        variant='rectangular'
        width={150}
        height={44}
        sx={{ borderRadius: '8px' }}
      />
      <Skeleton
        variant='rectangular'
        width={150}
        height={44}
        sx={{ borderRadius: '8px' }}
      />
    </PremiumRow>

    <BenefitsGrid>
      {Array.from({ length: 4 }, (_, i) => (
        <BenefitCell key={i}>
          <Skeleton variant='text' width='70%' height={16} />
          <Skeleton variant='text' width='50%' height={22} />
        </BenefitCell>
      ))}
    </BenefitsGrid>

    <CardFooter>
      <Skeleton
        variant='rectangular'
        width={110}
        height={36}
        sx={{ borderRadius: '8px' }}
      />
      <Skeleton
        variant='rectangular'
        width={130}
        height={36}
        sx={{ borderRadius: '8px' }}
      />
    </CardFooter>
  </Card>
)

// ─── Filter sidebar skeleton ─────────────────────────────────────────────────

const FilterSkeleton: React.FC = () => (
  <FilterSidebarFixed>
    <Skeleton variant='text' width='60%' height={24} />
    {Array.from({ length: 5 }, (_, i) => (
      <FilterGroup key={i}>
        <Skeleton variant='text' width='50%' height={18} />
        <Skeleton
          variant='rectangular'
          width='100%'
          height={36}
          sx={{ borderRadius: '8px' }}
        />
        {i % 2 === 0 && (
          <Skeleton
            variant='rectangular'
            width='100%'
            height={36}
            sx={{ borderRadius: '8px' }}
          />
        )}
      </FilterGroup>
    ))}
  </FilterSidebarFixed>
)

// ─── Exported full-page skeleton ─────────────────────────────────────────────

const PlanCardSkeleton: React.FC<{
  count?: number
  topBanner?: React.ReactNode
  showSidebar?: boolean
}> = ({ count = 4, topBanner, showSidebar = true }) => {
  if (!showSidebar) {
    return (
      <>
        {topBanner}
        {Array.from({ length: count }, (_, i) => (
          <SingleCardSkeleton key={i} />
        ))}
      </>
    )
  }

  return (
    <PageWrapper>
      <FilterPlaceholder>
        <FilterSkeleton />
      </FilterPlaceholder>
      <CardsArea>
        {topBanner}
        {Array.from({ length: count }, (_, i) => (
          <SingleCardSkeleton key={i} />
        ))}
      </CardsArea>
    </PageWrapper>
  )
}

export default PlanCardSkeleton
